<!-- =========================================================================================
  File Name: ClusteringWizard.vue
  Description: Complete wizard for creating clustering with step-by-step flow
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-30
========================================================================================== -->

<template>
	<div class="clustering-wizard-container">
		<!-- Wizard Header with Stepper -->
		<div class="wizard-header">
			<!-- Minimize Button -->
			<vs-button
				class="minimize-btn"
				color="primary"
				type="gradient"
				:title="$t('Wizard.Minimize')"
				@click="$emit('minimize')"
			>
				<feather-icon icon="ChevronsRightIcon" svgClasses="w-5 h-5" />
			</vs-button>
			<div class="wizard-stepper mt-2">
				<div
					v-for="(step, index) in steps"
					:key="index"
					class="step-item"
					:class="{
						active: currentStep === index,
						completed: currentStep > index,
						disabled: currentStep < index || currentStep === 4,
					}"
					@click="canNavigateToStep(index) && goToStep(index)"
				>
					<div class="step-circle">
						<feather-icon v-if="currentStep > index" icon="CheckIcon" svgClasses="w-5 h-5" />
						<span v-else>{{ index + 1 }}</span>
					</div>
					<div class="step-content">
						<span class="step-title">{{ $t(step.title) }}</span>
						<span class="step-subtitle">{{ $t(step.subtitle) }}</span>
					</div>
					<div v-if="index < steps.length - 1" class="step-connector"></div>
				</div>
			</div>
		</div>

		<!-- Wizard Body (Dynamic Step Content) -->
		<div class="wizard-body">
			<transition :name="transitionName" mode="out-in">
				<component
					ref="currentStep"
					:is="currentStepComponent"
					:key="currentStep"
					:wizard-data="wizardData"
					:map="map"
					:deck-overlay="deckOverlay"
					@update-data="updateWizardData"
					@next-step="handleNextStep"
					@prev-step="handlePrevStep"
					@validate="handleValidation"
					@load-points-to-map="$emit('load-points-to-map', $event)"
					@start-polygon-drawing="$emit('start-polygon-drawing')"
					@finish-polygon-drawing="$emit('finish-polygon-drawing')"
					@undo-last-polygon="$emit('undo-last-polygon')"
					@clear-all-polygons="$emit('clear-all-polygons')"
					@start-click-create="$emit('start-click-create')"
					@stop-click-create="$emit('stop-click-create')"
					@zoom-to-point="$emit('zoom-to-point', $event)"
					@view-on-map="handleViewOnMap"
					@export-result="handleExportResult"
					@create-another="handleCreateAnother"
					@view-list="handleViewList"
				/>
			</transition>
		</div>

		<!-- Wizard Footer (Navigation) -->
		<div class="wizard-footer">
			<div class="footer-left">
				<!-- Cancel Button (visible except on result step) -->
				<vs-button
					v-if="currentStep < 4"
					color="danger"
					type="border"
					icon-pack="feather"
					icon="icon-x"
					@click="handleCancel"
				>
					{{ $t("Cancel") }}
				</vs-button>

				<!-- Previous Button -->
				<vs-button
					v-if="currentStep > 0 && currentStep < 4"
					color="dark"
					type="border"
					icon-pack="feather"
					icon="icon-arrow-left"
					@click="handlePrevStep"
				>
					{{ $t("Wizard.Previous") }}
				</vs-button>
			</div>
			<div class="footer-right">
				<vs-button
					v-if="currentStep < 3"
					color="primary"
					type="gradient"
					icon-pack="feather"
					icon="icon-arrow-right"
					icon-after
					:disabled="!canProceed"
					@click="handleNextStep"
				>
					{{ $t("Wizard.Next") }}
				</vs-button>

				<vs-button
					v-if="currentStep === 3"
					color="success"
					type="gradient"
					icon-pack="feather"
					icon="icon-zap"
					:disabled="!canGenerate"
					@click="handleGenerate"
				>
					{{ $t("Wizard.Generate") }}
				</vs-button>

				<vs-button
					v-if="currentStep === 4"
					color="primary"
					icon-pack="feather"
					icon="icon-x"
					@click="$emit('close')"
				>
					{{ $t("Wizard.Close") }}
				</vs-button>
			</div>
		</div>

		<!-- Progress Overlay -->
		<transition name="fade">
			<div v-if="isGenerating" class="wizard-loading-overlay">
				<div class="loading-content">
					<vs-progress indeterminate color="primary" />
					<h3>{{ $t("Wizard.Generating") }}</h3>
					<p>{{ $t("Wizard.GeneratingMessage") }}</p>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import WizardStepOrigin from "@/components/clustering/wizard/WizardStepOrigin.vue";
import WizardStepSelection from "@/components/clustering/wizard/WizardStepSelection.vue";
import WizardStepDemand from "@/components/clustering/wizard/WizardStepDemand.vue";
import WizardStepConfig from "@/components/clustering/wizard/WizardStepConfig.vue";
import WizardStepResult from "@/components/clustering/wizard/WizardStepResult.vue";

export default {
	name: "ClusteringWizard",
	components: {
		WizardStepOrigin,
		WizardStepSelection,
		WizardStepDemand,
		WizardStepConfig,
		WizardStepResult,
	},
	props: {
		map: {
			type: Object,
			default: null,
		},
		deckOverlay: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			currentStep: 0,
			transitionName: "slide-next",
			isGenerating: false,
			steps: [
				{
					title: "Wizard.Steps.Origin.Title",
					subtitle: "Wizard.Steps.Origin.Subtitle",
					component: "WizardStepOrigin",
					validation: () => this.validateOriginStep(),
				},
				{
					title: "Wizard.Steps.Selection.Title",
					subtitle: "Wizard.Steps.Selection.Subtitle",
					component: "WizardStepSelection",
					validation: () => this.validateSelectionStep(),
				},
				{
					title: "Wizard.Steps.Demand.Title",
					subtitle: "Wizard.Steps.Demand.Subtitle",
					component: "WizardStepDemand",
					validation: () => true, // Optional step
				},
				{
					title: "Wizard.Steps.Config.Title",
					subtitle: "Wizard.Steps.Config.Subtitle",
					component: "WizardStepConfig",
					validation: () => this.validateConfigStep(),
				},
				{
					title: "Wizard.Steps.Result.Title",
					subtitle: "Wizard.Steps.Result.Subtitle",
					component: "WizardStepResult",
					validation: () => true,
				},
			],
			wizardData: {
				// Step 1: Origin
				loadedPoints: [],
				loadMethod: null, // 'all' or 'filtered'

				// Step 2: Selection
				selectedPoints: [],
				selectionMethod: null, // 'polygon', 'click', 'table', 'all'
				drawnPolygons: [],

				// Step 3: Demand
				demandConfigured: false,
				totalDemand: 0,

				// Step 4: Config
				clusteringName: "",
				totalClusters: 3,
				totalMicroClusters: null,

				// Step 5: Result
				clusteringResult: null,
				clusteredPoints: [],
			},
		};
	},
	computed: {
		currentStepComponent() {
			return this.steps[this.currentStep].component;
		},
		canProceed() {
			return this.steps[this.currentStep].validation();
		},
		canGenerate() {
			return this.validateConfigStep();
		},
	},
	methods: {
		/**
		 * Update wizard data from child components
		 */
		updateWizardData(data) {
			this.wizardData = {
				...this.wizardData,
				...data,
			};

			// Emit sync event to parent map whenever loadedPoints or selectedPoints change
			if (data.loadedPoints !== undefined || data.selectedPoints !== undefined) {
				this.$emit("sync-points", {
					loadedPoints: this.wizardData.loadedPoints,
					selectedPoints: this.wizardData.selectedPoints,
				});
			}
		},

		/**
		 * Navigate to specific step
		 */
		goToStep(stepIndex) {
			if (stepIndex < this.currentStep) {
				// Can always go back
				this.transitionName = "slide-prev";
				this.currentStep = stepIndex;
			} else if (stepIndex > this.currentStep) {
				// Can only go forward if current step is valid
				if (this.canProceed) {
					this.transitionName = "slide-next";
					this.currentStep = stepIndex;
				}
			}
		},

		/**
		 * Check if can navigate to step
		 */
		canNavigateToStep(stepIndex) {
			// Prevent navigation if on result step
			if (this.currentStep === 4) return false;

			// Can always navigate backwards
			if (stepIndex < this.currentStep) return true;

			// Can't skip steps forward
			if (stepIndex > this.currentStep + 1) return false;

			// Can navigate to next step if current is valid
			return stepIndex === this.currentStep + 1 && this.canProceed;
		},

		/**
		 * Handle next step
		 */
		handleNextStep() {
			if (this.canProceed && this.currentStep < this.steps.length - 1) {
				this.transitionName = "slide-next";
				this.currentStep++;

				// Emit event for tracking
				this.$emit("step-change", {
					from: this.currentStep - 1,
					to: this.currentStep,
					data: this.wizardData,
				});
			}
		},

		/**
		 * Handle previous step
		 */
		handlePrevStep() {
			if (this.currentStep > 0) {
				this.transitionName = "slide-prev";
				this.currentStep--;

				this.$emit("step-change", {
					from: this.currentStep + 1,
					to: this.currentStep,
					data: this.wizardData,
				});
			}
		},

		/**
		 * Handle validation from child
		 */
		handleValidation(isValid) {
			// Child component can report validation status
			if (!isValid) {
				this.$vs.notify({
					color: "warning",
					title: this.$t("Warning"),
					text: this.$t("Wizard.ValidationError"),
				});
			}
		},

		/**
		 * Validate Origin Step
		 */
		validateOriginStep() {
			// Allow to proceed if user selected "skip" method (will load from DB in next step)
			if (this.wizardData.loadMethod === "skip") {
				return true;
			}
			// Otherwise, require loaded points
			return this.wizardData.loadedPoints && this.wizardData.loadedPoints.length > 0;
		},

		/**
		 * Validate Selection Step
		 */
		validateSelectionStep() {
			return this.wizardData.selectedPoints && this.wizardData.selectedPoints.length >= 1;
		},

		/**
		 * Validate Config Step
		 */
		validateConfigStep() {
			return (
				this.wizardData.clusteringName.length > 0 &&
				this.wizardData.totalClusters > 0 &&
				this.wizardData.selectedPoints.length > 0
			);
		},

		/**
		 * Handle Generate Clustering
		 */
		async handleGenerate() {
			this.isGenerating = true;

			try {
				// Emit generate event with all wizard data
				this.$emit("generate-clustering", this.wizardData);

				// Wait for parent to handle generation
				// Parent will update wizardData.clusteringResult when done
			} catch (error) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: error.message || this.$t("Wizard.GenerateError"),
				});
				this.isGenerating = false;
			}
		},

		/**
		 * Mark generation as complete (called by parent)
		 */
		onGenerationComplete(result) {
			this.isGenerating = false;
			this.wizardData.clusteringResult = result;
			this.transitionName = "slide-next";
			this.currentStep = 4; // Go to result step
		},

		/**
		 * Mark generation as failed (called by parent)
		 */
		onGenerationFailed() {
			this.isGenerating = false;
		},

		/**
		 * Handle cancel - confirm and close wizard
		 */
		handleCancel() {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Confirm"),
				text: this.$t("Wizard.ConfirmCancel"),
				accept: () => {
					this.resetWizard();
					this.$emit("cancel");
				},
				acceptText: this.$t("Confirm"),
				cancelText: this.$t("Cancel"),
			});
		},

		/**
		 * Reset wizard
		 */
		resetWizard() {
			this.currentStep = 0;
			this.transitionName = "slide-next";
			this.wizardData = {
				loadedPoints: [],
				loadMethod: null,
				selectedPoints: [],
				selectionMethod: null,
				drawnPolygons: [],
				demandConfigured: false,
				totalDemand: 0,
				clusteringName: "",
				totalClusters: 3,
				totalMicroClusters: null,
				clusteringResult: null,
				clusteredPoints: [],
			};

			// Sync empty points to map
			this.$emit("sync-points", {
				loadedPoints: [],
				selectedPoints: [],
			});

			// Clear polygons on map
			this.$emit("clear-all-polygons");
		},

		/**
		 * Handle View on Map action from Result step
		 */
		handleViewOnMap() {
			// Close wizard and let the map show the clustering
			// this.$emit("close"); // Handled by parent after loading
			const clusteringResult = {
				id: this.wizardData.clusteringResult.id,
				status: this.wizardData.clusteringResult.status,
				name: this.wizardData.clusteringName,
				total_points: this.wizardData.selectedPoints.length,
				total_clusters: this.wizardData.totalClusters,
				total_micro_clusters: this.wizardData.totalMicroClusters,
			};
			this.$emit("view-on-map", clusteringResult);
		},

		/**
		 * Handle Export Result action
		 */
		handleExportResult() {
			// Emit event to parent to handle export
			this.$emit("export-clustering", this.wizardData.clusteringResult);
		},

		/**
		 * Handle Create Another Clustering action
		 */
		handleCreateAnother() {
			this.$vs.dialog({
				type: "confirm",
				color: "primary",
				title: this.$t("Confirm"),
				text: this.$t("Wizard.ConfirmCreateAnother"),
				acceptText: this.$t("Confirm"),
				cancelText: this.$t("Cancel"),
				accept: () => {
					this.resetWizard();
				},
			});
		},

		/**
		 * Handle View List action
		 */
		handleViewList() {
			this.$emit("close");
			this.$emit("open-cluster-list");
		},

		/**
		 * Stop drawing mode in current step
		 */
		stopDrawingMode() {
			if (this.$refs.currentStep && typeof this.$refs.currentStep.stopDrawingMode === "function") {
				this.$refs.currentStep.stopDrawingMode();
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.clustering-wizard-container {
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: 600px;
	background: white;
	z-index: 54000;
	display: flex;
	flex-direction: column;
	box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
	overflow: hidden;
}

.wizard-header {
	background: white;
	width: 100%;
	border-bottom: 2px solid #e0e0e0;
	padding: 20px;
	flex-shrink: 0;
	position: relative;

	.minimize-btn {
		position: absolute;
		top: 16%;
		right: 0px;
		transform: translateY(-50%);
		z-index: 10;
		width: 50px;
		height: 40px;
		box-shadow: 0 4px 16px rgba(var(--vs-primary), 0.4);
		padding: 0 !important;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
		animation: pulse 2s infinite;

		&:hover {
			box-shadow: 0 6px 20px rgba(var(--vs-primary), 0.6);
			animation: none;

			svg {
				transform: translateX(3px);
			}
		}
	}

	@keyframes pulse {
		0%,
		100% {
			box-shadow: 0 4px 16px rgba(var(--vs-primary), 0.4);
		}
		50% {
			box-shadow: 0 4px 16px rgba(var(--vs-primary), 0.8), 0 0 0 8px rgba(var(--vs-primary), 0);
		}
	}
}

.wizard-stepper {
	display: grid;
	grid-template-columns: repeat(5, 1fr);
	gap: 8px;
	position: relative;
}

.step-item {
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
	cursor: pointer;
	transition: all 0.3s ease;
	padding: 8px 4px;

	&.disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}

	&:hover:not(.disabled) {
		background: #f5f5f5;
		border-radius: 8px;
	}

	.step-circle {
		width: 36px;
		height: 36px;
		border-radius: 50%;
		background: #e0e0e0;
		color: #666;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1rem;
		font-weight: 700;
		margin-bottom: 8px;
		transition: all 0.3s ease;
		z-index: 2;
	}

	&.active .step-circle {
		background: rgba(var(--vs-primary), 1);
		color: white;
		box-shadow: 0 4px 12px rgba(var(--vs-primary), 0.4);
		transform: scale(1.1);
	}

	&.completed .step-circle {
		background: rgba(var(--vs-success), 1);
		color: white;
	}

	.step-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;

		.step-title {
			font-size: 0.7rem;
			font-weight: 600;
			color: #333;
			margin-bottom: 2px;
			line-height: 1.2;
		}

		.step-subtitle {
			font-size: 0.65rem;
			color: #999;
			display: none; // Hide subtitle to save space
		}
	}

	&.active .step-content .step-title {
		color: var(--vs-primary);
	}

	&.completed .step-content .step-title {
		color: #4caf50;
	}

	.step-connector {
		display: none; // Remove connectors for compact layout
	}
}

.wizard-body {
	background: white;
	width: 100%;
	flex: 1;
	overflow-y: auto;
	padding: 15px;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: #f1f1f1;
	}

	&::-webkit-scrollbar-thumb {
		background: #888;
		border-radius: 4px;

		&:hover {
			background: #555;
		}
	}
}

.wizard-footer {
	background: white;
	width: 100%;
	border-top: 2px solid #e0e0e0;
	padding: 16px 20px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;

	.footer-left {
		flex: 0 0 auto;
		display: flex;
		gap: 8px;
	}

	.footer-center {
		flex: 1;
		display: flex;
		justify-content: center;

		.step-indicator {
			font-size: 0.85rem;
			color: #666;
			font-weight: 500;
		}
	}

	.footer-right {
		flex: 0 0 auto;
		display: flex;
		justify-content: flex-end;
	}
}

.wizard-loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(255, 255, 255, 0.95);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	border-radius: 12px;

	.loading-content {
		text-align: center;
		padding: 40px;

		h3 {
			margin: 20px 0 10px 0;
			font-size: 1.5rem;
			color: var(--vs-primary);
		}

		p {
			color: #666;
			font-size: 1rem;
		}
	}
}

// Transitions
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
	transition: all 0.3s ease;
}

.slide-next-enter {
	transform: translateX(100%);
	opacity: 0;
}

.slide-next-leave-to {
	transform: translateX(-100%);
	opacity: 0;
}

.slide-prev-enter {
	transform: translateX(-100%);
	opacity: 0;
}

.slide-prev-leave-to {
	transform: translateX(100%);
	opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

// Responsive
@media screen and (max-width: 1024px) {
	.clustering-wizard-container {
		width: 500px;
	}
}

@media screen and (max-width: 768px) {
	.clustering-wizard-container {
		width: 100%;
		left: 0;
	}

	.wizard-stepper {
		grid-template-columns: repeat(5, 1fr);
		gap: 4px;
	}

	.step-item {
		.step-circle {
			width: 28px;
			height: 28px;
			font-size: 0.85rem;
		}

		.step-content .step-title {
			font-size: 0.6rem;
		}
	}

	.wizard-footer {
		flex-wrap: wrap;

		.footer-center {
			order: -1;
			flex-basis: 100%;
			margin-bottom: 8px;
		}
	}
}
</style>
